/**
 * ManagerFlaggedTab — RULE-FORECAST-1 manager surfacing.
 *
 * Lists every account in the manager's tenant whose
 * `accounts.manager_attention_flag = true`. Two flag triggers:
 *   1. CP10 timing assessment came in at 50% (auto-set by trigger
 *      `fn_auto_flag_50pct_timing`).
 *   2. Rep attempted a bucket above the timing-eligible floor and the
 *      system auto-downgraded.
 *
 * A manager can clear the flag inline (sets manager_attention_flag = false
 * and nulls manager_flag_reason).
 */
import { useEffect, useState } from "react";
import { supabase, getCurrentTenant } from "@/lib/session";

const BLACK = "#0B0B0F";
const RED = "#DC2626";
const SLATE = "#6B6B70";

interface FlaggedRow {
  id: number;
  name: string;
  owner_user_id: string | null;
  manager_flag_reason: string | null;
  forecast_category_override: string | null;
  composite_score: number;
  timing_pct: number | null;
}

export function ManagerFlaggedTab() {
  const [rows, setRows] = useState<FlaggedRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [clearingId, setClearingId] = useState<number | null>(null);

  async function load() {
    setError(null);
    try {
      const tenantId = getCurrentTenant?.() ?? null;
      if (!tenantId) {
        setRows([]);
        return;
      }
      const sb = supabase();
      // Read the gated view so we get current composite and timing alongside.
      const { data: forecastRows, error: viewErr } = await sb
        .from("v_account_forecast")
        .select("account_id,account_name,ae_user_id,composite_score,timing_pct,forecast_category_override,manager_attention_flag")
        .eq("tenant_id", tenantId)
        .eq("manager_attention_flag", true);
      if (viewErr) throw viewErr;

      const ids = (forecastRows ?? []).map((r: any) => r.account_id);
      const { data: reasonRows } = ids.length
        ? await sb
            .from("accounts")
            .select("id,manager_flag_reason")
            .in("id", ids)
        : { data: [] as any[] };
      const reasonById = new Map<number, string | null>();
      for (const r of reasonRows ?? []) reasonById.set(r.id, r.manager_flag_reason ?? null);

      setRows(
        (forecastRows ?? []).map((r: any): FlaggedRow => ({
          id: r.account_id,
          name: r.account_name,
          owner_user_id: r.ae_user_id,
          manager_flag_reason: reasonById.get(r.account_id) ?? null,
          forecast_category_override: r.forecast_category_override,
          composite_score: Number(r.composite_score ?? 0),
          timing_pct: r.timing_pct == null ? null : Number(r.timing_pct),
        })),
      );
    } catch (err: any) {
      console.error("[ManagerFlaggedTab]", err);
      setError(err?.message ?? "Failed to load flagged accounts");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function clearFlag(id: number) {
    setClearingId(id);
    try {
      const sb = supabase();
      const { error } = await sb
        .from("accounts")
        .update({ manager_attention_flag: false, manager_flag_reason: null })
        .eq("id", id);
      if (error) throw error;
      await load();
    } catch (err: any) {
      console.error("[ManagerFlaggedTab] clearFlag", err);
      setError(err?.message ?? "Failed to clear flag");
    } finally {
      setClearingId(null);
    }
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h2 className="text-xl font-bold mb-2" style={{ color: BLACK }}>
        Manager attention queue
      </h2>
      <p className="text-sm mb-4" style={{ color: SLATE }}>
        Accounts flagged by RULE-FORECAST-1. Triggers: CP10 timing came in at 50%, or a rep tried to place the deal in a bucket above its timing-eligible floor.
      </p>

      {error && (
        <div
          role="alert"
          className="mb-4 p-3 rounded text-sm"
          style={{ background: `${RED}10`, color: BLACK, border: `1px solid ${RED}55` }}
        >
          {error}
        </div>
      )}

      {!rows ? (
        <div style={{ color: SLATE }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-lg p-6 text-center"
          style={{ background: "#fff", border: `1px solid ${BLACK}1A`, color: SLATE }}
        >
          No accounts currently flagged.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg" style={{ background: "#fff", border: `1px solid ${BLACK}1A` }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: `1px solid ${BLACK}1A` }}>
                <th className="text-left px-3 py-2" style={{ color: SLATE }}>Account</th>
                <th className="text-left px-3 py-2" style={{ color: SLATE }}>Composite</th>
                <th className="text-left px-3 py-2" style={{ color: SLATE }}>Timing</th>
                <th className="text-left px-3 py-2" style={{ color: SLATE }}>Rep pick</th>
                <th className="text-left px-3 py-2" style={{ color: SLATE }}>Reason</th>
                <th className="text-left px-3 py-2" style={{ color: SLATE }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`flagged-row-${r.id}`} style={{ borderBottom: `1px solid ${BLACK}0A` }}>
                  <td className="px-3 py-2 font-semibold" style={{ color: BLACK }}>
                    <a href={`/#/account/${r.id}`} style={{ color: BLACK, textDecoration: "underline" }}>{r.name}</a>
                  </td>
                  <td className="px-3 py-2 tabular-nums">{r.composite_score}</td>
                  <td className="px-3 py-2 tabular-nums">
                    {r.timing_pct == null ? "—" : r.timing_pct < 50 ? "<50%" : `${r.timing_pct}%`}
                  </td>
                  <td className="px-3 py-2">{r.forecast_category_override ?? "—"}</td>
                  <td className="px-3 py-2 text-xs" style={{ color: BLACK }}>
                    <span aria-hidden style={{ color: RED }}>●</span> {r.manager_flag_reason ?? "Manager-set"}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => clearFlag(r.id)}
                      disabled={clearingId === r.id}
                      data-testid={`flagged-clear-${r.id}`}
                      className="text-xs font-semibold px-3 py-1 rounded"
                      style={{
                        background: "#fff",
                        color: BLACK,
                        border: `1px solid ${BLACK}33`,
                        cursor: clearingId === r.id ? "not-allowed" : "pointer",
                      }}
                    >
                      {clearingId === r.id ? "Clearing…" : "Clear flag"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
